import { useCallback } from 'react';
import { createLocalStorageProvider } from '@atlassian/jira-browser-storage-providers/src/controllers/local-storage/index.tsx';

type ColumnSortDirection = 'ASC' | 'DESC' | 'NONE';

const COLUMN_SORT_CONFIGS_LS_KEY = 'COLUMN_SORT_CONFIG';

interface SortConfig {
	sortColumnFromLS: string | null;
	sortDirectionFromLS: ColumnSortDirection | null;
}

export const useTableColumnSortConfigsFromLS = (
	STORAGE_PREFIX: string,
): {
	setSortConfig: (field: string, direction: string) => void;
	getSortConfig: () => SortConfig;
} => {
	const localStorageProvider = createLocalStorageProvider(STORAGE_PREFIX);

	const setSortConfig = useCallback(
		(field: string, sortDirection: string) => {
			localStorageProvider.remove(COLUMN_SORT_CONFIGS_LS_KEY);

			// Don't set in LS if sorting is cleared
			if (sortDirection === 'NONE') {
				return;
			}

			localStorageProvider.set(COLUMN_SORT_CONFIGS_LS_KEY, `${field}=${sortDirection}`);
		},
		[localStorageProvider],
	);
	const getSortConfig = useCallback((): SortConfig => {
		const localStorageKey = localStorageProvider.get(COLUMN_SORT_CONFIGS_LS_KEY);

		if (!localStorageKey) {
			return { sortColumnFromLS: null, sortDirectionFromLS: null };
		}

		const [sortColumn, sortDirection] = localStorageKey?.split('=');
		return { sortColumnFromLS: sortColumn, sortDirectionFromLS: sortDirection };
	}, [localStorageProvider]);

	return {
		setSortConfig,
		getSortConfig,
	};
};
